/**
 * API interceptor to capture fetch requests/responses for feedback submission
 */
/* global window */

import { ApiLogEntry } from '../types/feedback';

const apiLogs: ApiLogEntry[] = [];
const MAX_API_LOGS = 50;

let originalFetch: typeof fetch;
let isInitialized = false;

/**
 * Initialize API interceptor
 * Should be called once at app startup
 */
export function initApiInterceptor(): void {
  if (isInitialized) {
    return;
  }

  // Store original fetch
  originalFetch = window.fetch;

  // Intercept fetch globally
  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const [url, options] = args;
    const timestamp = new Date();

    try {
      const response = await originalFetch(...args);

      // Clone response to read body without consuming it
      const clonedResponse = response.clone();
      let responseBody: any = null;

      try {
        responseBody = await clonedResponse.json();
      } catch {
        // Response is not JSON, skip
      }

      // Parse request body if present
      let requestBody: any = null;
      if (options?.body) {
        try {
          requestBody = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
        } catch {
          // Body is not JSON, skip
        }
      }

      // Log request/response
      const logEntry: ApiLogEntry = {
        url: url.toString(),
        method: options?.method || 'GET',
        status: response.status,
        timestamp,
        request: requestBody,
        response: responseBody,
      };

      apiLogs.push(logEntry);

      // Keep only last MAX_API_LOGS entries
      if (apiLogs.length > MAX_API_LOGS) {
        apiLogs.shift();
      }

      return response;
    } catch (error) {
      // Log error
      const logEntry: ApiLogEntry = {
        url: url.toString(),
        method: options?.method || 'GET',
        status: 0,
        timestamp,
        error: error instanceof Error ? error.message : 'Unknown error',
      };

      apiLogs.push(logEntry);

      if (apiLogs.length > MAX_API_LOGS) {
        apiLogs.shift();
      }

      throw error;
    }
  };

  isInitialized = true;
  console.log('API interceptor initialized');
}

/**
 * Get captured API logs
 */
export function getApiLogs(): ApiLogEntry[] {
  return [...apiLogs];
}

/**
 * Clear captured API logs
 */
export function clearApiLogs(): void {
  apiLogs.length = 0;
}
